/**
 * DropzoneOverlay — Full-viewport drag-and-drop overlay.
 *
 * Activates whenever the user drags files into the browser window.
 * Matches WeTransfer's immersive drop experience:
 *   - Translucent blurred backdrop (backdropFilter: 'blur(16px)')
 *   - High-contrast dashed border
 *   - SVG stroke upload icon + bold header & subtitle
 */
import React, { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Spacing, FontSize, FontWeight, Radius } from '../theme/tokens';
import { Icon } from '../atoms/Icon';
import { useTheme } from '../hooks/useTheme';

interface DropzoneOverlayProps {
  onDrop: (files: FileList) => void;
  children: React.ReactNode;
}

export const DropzoneOverlay: React.FC<DropzoneOverlayProps> = ({ onDrop, children }) => {
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);
  const { colors, isDark } = useTheme();

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current += 1;
      if (e.dataTransfer?.items && e.dataTransfer.items.length > 0) {
        setIsDragging(true);
      }
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current -= 1;
      if (dragCounter.current === 0) {
        setIsDragging(false);
      }
    };

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      dragCounter.current = 0;

      if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
        onDrop(e.dataTransfer.files);
      }
    };

    window.addEventListener('dragenter', handleDragEnter);
    window.addEventListener('dragleave', handleDragLeave);
    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('drop', handleDrop);

    return () => {
      window.removeEventListener('dragenter', handleDragEnter);
      window.removeEventListener('dragleave', handleDragLeave);
      window.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('drop', handleDrop);
    };
  }, [onDrop]);

  return (
    <View style={styles.container}>
      {children}
      {isDragging && (
        <View
          style={[
            styles.overlay,
            {
              backgroundColor: isDark
                ? 'rgba(0, 0, 0, 0.75)'
                : 'rgba(255, 255, 255, 0.85)',
            },
          ]}
        >
          <View
            style={[
              styles.dropBox,
              {
                borderColor: colors.text,
                backgroundColor: isDark
                  ? 'rgba(255, 255, 255, 0.04)'
                  : 'rgba(0, 0, 0, 0.03)',
              },
            ]}
          >
            <View
              style={[
                styles.iconCircle,
                { backgroundColor: colors.brand },
              ]}
            >
              <Icon name="upload" size={32} color={colors.brandText as string} strokeWidth={2.5} />
            </View>
            <Text style={[styles.dropTitle, { color: colors.text }]}>
              Drop files to send
            </Text>
            <Text style={[styles.dropSubtitle, { color: colors.subtext }]}>
              Direct peer-to-peer · Unlimited file size
            </Text>
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  overlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 99999,
    justifyContent: 'center',
    alignItems: 'center',
    padding: Spacing.xl,
    backdropFilter: 'blur(16px)',
  } as any,
  dropBox: {
    borderWidth:     2,
    borderStyle:     'dashed',
    borderRadius:    Radius.xxl + 4,
    width:           '100%',
    height:          '100%',
    maxWidth:        680,
    maxHeight:       480,
    justifyContent:  'center',
    alignItems:      'center',
    padding:         Spacing.xl,
  },
  iconCircle: {
    width:          64,
    height:         64,
    borderRadius:   32,
    justifyContent: 'center',
    alignItems:     'center',
    marginBottom:   Spacing.lg,
    shadowColor:    '#000000',
    shadowOffset:   { width: 0, height: 4 },
    shadowOpacity:  0.15,
    shadowRadius:   8,
    elevation:      4,
  },
  dropTitle: {
    fontSize:      FontSize.xxl,
    fontWeight:    FontWeight.bold,
    letterSpacing: -0.5,
    textAlign:     'center',
    marginBottom:  Spacing.xs,
  },
  dropSubtitle: {
    fontSize:      FontSize.sm + 1,
    fontWeight:    FontWeight.medium,
    textAlign:     'center',
    letterSpacing: -0.1,
  },
});
